import axios from 'axios';

export const BACKEND_URL = window.__APP_CONFIG__?.API_URL || process.env.REACT_APP_BACKEND_URL;
export const API_URL = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      if (window.location.pathname.startsWith('/admin')) {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (name, email, password) => api.post('/auth/register', { name, email, password }),
  getMe: () => api.get('/auth/me'),
  changePassword: (current_password, new_password) => api.post('/auth/change-password', { current_password, new_password }),
};

// Doctor Dashboard API (uses doctor_token)
const doctorApi = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

doctorApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('doctor_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

doctorApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('doctor_token');
      localStorage.removeItem('doctor_user');
      if (window.location.pathname.startsWith('/dashboard')) {
        window.location.href = '/dashboard/login';
      }
    }
    return Promise.reject(error);
  }
);

export const doctorAuthAPI = {
  setPassword: (token, password) => api.post('/doctor/set-password', { token, password }),
  requestPasswordLink: (email) => api.post('/doctor/request-password-link', { email }),
  login: (email, password) => api.post('/doctor/login', { email, password }),
  getMe: () => doctorApi.get('/doctor/me'),
  updateProfile: (data) => doctorApi.put('/doctor/profile', data),
  getReviews: () => doctorApi.get('/doctor/reviews'),
  getInquiries: () => doctorApi.get('/doctor/inquiries'),
  uploadGalleryImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return doctorApi.post('/doctor/gallery', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  removeGalleryImage: (imageUrl) => doctorApi.delete('/doctor/gallery', { params: { image_url: imageUrl } }),
};

// Doctors API
export const doctorsAPI = {
  getAll: (params) => api.get('/doctors', { params }),
  getById: (id) => api.get(`/doctors/${id}`),
  getBySlug: (slug) => api.get(`/doctors/slug/${slug}`),
  create: (data) => api.post('/doctors', data),
  update: (id, data) => api.put(`/doctors/${id}`, data),
  delete: (id) => api.delete(`/doctors/${id}`),
  approve: (id) => api.post(`/doctors/${id}/approve`),
  reject: (id) => api.post(`/doctors/${id}/reject`),
  getCount: (status) => api.get('/doctors/count', { params: { status } }),
  uploadGalleryImage: (doctorId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/doctors/${doctorId}/gallery`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  removeGalleryImage: (doctorId, imageUrl) => api.delete(`/doctors/${doctorId}/gallery`, { params: { image_url: imageUrl } }),
};

// File Upload API
export const uploadAPI = {
  upload: (file, folder = 'gallery', doctorId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    const params = new URLSearchParams();
    params.append('folder', folder);
    if (doctorId) params.append('doctor_id', doctorId);
    return api.post(`/upload?${params.toString()}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Public doctor registration
export const registerDoctor = (data) => api.post('/register-doctor', data);

// Specialities API
export const specialitiesAPI = {
  getAll: (category) => api.get('/specialities', { params: { category } }),
  create: (data) => api.post('/specialities', data),
  delete: (id) => api.delete(`/specialities/${id}`),
};

// Locations API
export const locationsAPI = {
  getAll: (country) => api.get('/locations', { params: { country } }),
  create: (data) => api.post('/locations', data),
  delete: (id) => api.delete(`/locations/${id}`),
};

// SEO Pages API
export const seoPagesAPI = {
  getAll: (params) => api.get('/seo-pages', { params }),
  getBySlug: (slug) => api.get(`/seo-pages/${slug}`),
  create: (data) => api.post('/seo-pages', data),
  update: (id, data) => api.put(`/seo-pages/${id}`, data),
  delete: (id) => api.delete(`/seo-pages/${id}`),
};

// Blogs API
export const blogsAPI = {
  getAll: (params) => api.get('/blogs', { params }),
  getBySlug: (slug) => api.get(`/blogs/${slug}`),
  create: (data) => api.post('/blogs', data),
  update: (id, data) => api.put(`/blogs/${id}`, data),
  delete: (id) => api.delete(`/blogs/${id}`),
  getCount: (category) => api.get('/blogs/count', { params: { category } }),
};

// Testimonials API
export const testimonialsAPI = {
  getAll: (params) => api.get('/testimonials', { params }),
  create: (data) => api.post('/testimonials', data),
  delete: (id) => api.delete(`/testimonials/${id}`),
};

// Reviews API
export const reviewsAPI = {
  getAll: (params) => api.get('/reviews', { params }),
  getByDoctor: (doctorId) => api.get(`/reviews/doctor/${doctorId}`),
  create: (data) => api.post('/reviews', data),
  approve: (id) => api.put(`/reviews/${id}/approve`),
  delete: (id) => api.delete(`/reviews/${id}`),
};

// WhatsApp Tracking API
export const whatsappAPI = {
  track: (data) => api.post('/whatsapp-inquiries', data),
  getAll: (params) => api.get('/whatsapp-inquiries', { params }),
  getStats: () => api.get('/whatsapp-inquiries/stats'),
};

// Marketing Pages API
export const marketingPagesAPI = {
  getAll: (params) => api.get('/marketing-pages', { params }),
  getBySlug: (slug) => api.get(`/marketing-pages/${slug}`),
  create: (data) => api.post('/marketing-pages', data),
  update: (id, data) => api.put(`/marketing-pages/${id}`, data),
  delete: (id) => api.delete(`/marketing-pages/${id}`),
  bulkCreate: () => api.post('/marketing-pages/bulk-create'),
};

// CMS: blogs admin, landing SEO, site page SEO, redirects
export const cmsAPI = {
  listBlogs: (params) => api.get('/admin/blogs', { params }),
  getBlog: (id) => api.get(`/admin/blogs/${id}`),
  createBlog: (data) => api.post('/admin/blogs', data),
  updateBlog: (id, data) => api.put(`/admin/blogs/${id}`, data),
  toggleBlog: (id, action) => api.post(`/admin/blogs/${id}/${action}`),
  deleteBlog: (id) => api.delete(`/blogs/${id}`),
  getLanding: (id) => api.get(`/admin/landing-pages/${id}`),
  updateLanding: (id, data) => api.put(`/admin/landing-pages/${id}`, data),
  getPageSeo: (path) => api.get('/page-seo', { params: { path } }),
  pageSeoCatalog: () => api.get('/admin/page-seo/catalog'),
  savePageSeo: (data) => api.put('/admin/page-seo', data),
  resetPageSeo: (path) => api.delete('/admin/page-seo', { params: { path } }),
  listRedirects: () => api.get('/redirects'),
  resolveRedirect: (path) => api.get('/redirects/resolve', { params: { path } }),
  createRedirect: (data) => api.post('/admin/redirects', data),
  deleteRedirect: (id) => api.delete(`/admin/redirects/${id}`),
};

// Stats API
export const statsAPI = {
  get: () => api.get('/stats'),
};

// Seed API
export const seedAPI = {
  run: () => api.post('/seed'),
};

export default api;
